package com.exura.pages.planPreview

import androidx.compose.runtime.*
import com.exura.components.icons.InlineIcon
import com.exura.components.icons.UntiledIcon
import com.exura.components.layouts.plan.PlanConstructorLayoutScope
import com.exura.components.layouts.plan.SideBarPlanElement
import com.exura.components.pageTypography.*
import com.exura.features.plandefinition.domain.model.collection.Collection
import com.exura.features.plandefinition.domain.model.element.BasePlanElement
import com.exura.features.plandefinition.domain.model.series.BaseSeries
import com.exura.features.plandefinition.planLocalization
import com.exura.navigation.Route
import com.exura.navigation.parameters.currentPageParams
import com.exura.ui.Assets
import com.exura.ui.Dimens
import com.varabyte.kobweb.compose.css.ObjectFit
import com.varabyte.kobweb.compose.foundation.layout.Arrangement
import com.varabyte.kobweb.compose.foundation.layout.Box
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.ui.Alignment
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.*
import com.varabyte.kobweb.core.Page
import com.varabyte.kobweb.core.layout.Layout
import com.varabyte.kobweb.core.rememberPageContext
import com.varabyte.kobweb.silk.components.graphics.Image
import com.varabyte.kobweb.silk.components.text.SpanText
import com.varabyte.kobweb.silk.style.toModifier
import org.jetbrains.compose.web.css.cssRem
import org.jetbrains.compose.web.css.px


@Page(Route.WithParams.NESTED_SERIES_DETAILS)
@Layout(".components.layouts.plan.PlanConstructorLayout")
@Composable
fun PlanConstructorLayoutScope.NestedSeriesPreviewIndexPage() {
    val ctx = rememberPageContext()
    val planId = ctx.route.params[Route.Params.PLAN_ID] ?: return
    val seriesId = ctx.route.params[Route.Params.SERIES_ID] ?: return
    val collectionId = ctx.route.params[Route.Params.COLLECTION_ID] ?: return

    var selectedSeries by remember { mutableStateOf<BaseSeries<*>?>(null) }
    var parentCollection by remember { mutableStateOf<Collection?>(null) }

    // Set tooltip strategy (same as index page)
    val showTooltipContent: @Composable (BasePlanElement, Int) -> Unit = { spawnedElement, dayOffset ->
        PreviewElementTooltipContent(spawnedElement, dayOffset = dayOffset, imageFallback = planBuilder.imageUrl())
    }
    setTooltipRenderStrategy(showTooltipContent)

    // Set navigation strategy (same as index page)
    val navigationStrategy: (SideBarPlanElement) -> Unit = { clickedElement ->
        val basePath = when (clickedElement) {
            is SideBarPlanElement.RootSeriesItem -> Route.WithParams.toSeriesPreview(
                planId,
                null,
                clickedElement.seriesItem.firebaseDocId!!,
            )
            is SideBarPlanElement.CollectionItem -> Route.WithParams.toCollectionPreview(
                planId,
                clickedElement.collectionItem.firebaseDocId!!,
            )
            is SideBarPlanElement.NestedSeriesItem -> Route.WithParams.toSeriesPreview(
                planId,
                clickedElement.collectionItem.firebaseDocId!!,
                clickedElement.seriesItem.firebaseDocId!!,
            )
        }
        ctx.router.navigateTo("$basePath${currentPageParams()}")
    }
    setInternalNavigationStrategy(navigationStrategy)

    // Find and mark the nested series as selected
    LaunchedEffect(seriesId, collectionId, sideBarElements) {
        val nestedSeriesElement = sideBarElements.firstOrNull { element ->
            element is SideBarPlanElement.NestedSeriesItem &&
            element.seriesItem.firebaseDocId == seriesId &&
            element.collectionItem.firebaseDocId == collectionId
        }
        
        nestedSeriesElement?.let {
            markSelection(
                seriesPos = it.seriesPos,
                collectionPos = it.collectionPos
            )
            val nested = it as SideBarPlanElement.NestedSeriesItem
            selectedSeries = nested.seriesItem
            parentCollection = nested.collectionItem
        }
    }

    // Render series details
    selectedSeries?.let { series ->
        GeneralCardEntry(
            modifier = Modifier
                .maxWidth(20.cssRem)
                .padding(Dimens.planConstructorContentPadding)
        ) {
            Column(
                modifier = Modifier.fillMaxWidth(),
                verticalArrangement = Arrangement.spacedBy(16.px),
                horizontalAlignment = Alignment.Start
            ) {
                // Parent collection badge
                parentCollection?.let { collection ->
                    SpanText(
                        "Part of: ${collection.name}",
                        modifier = ExuraTextStyle.toModifier(
                            ExuraUnderwriteTextVariant,
                            ExuraCommentaryTextVariant,
                            ExuraSecondaryTextVariant
                        )
                    )
                }

                // Image
                series.imageUrl?.let { imgUrl ->
                    Box(
                        modifier = ListEntryImageStyle.toModifier()
                            .fillMaxWidth()
                            .height(200.px),
                        contentAlignment = Alignment.Center
                    ) {
                        Image(imgUrl, modifier = Modifier.objectFit(ObjectFit.Cover).fillMaxSize())
                    }
                }

                // Title
                SpanText(
                    series.title,
                    modifier = ExuraTextStyle.toModifier(
                        ExuraSectionHeaderTextVariant,
                        ExuraEmphasisedTextVariant,
                        ExuraBaseColorVariant
                    )
                )

                // Description
                if (series.description.isNotBlank()) {
                    SpanText(
                        series.description,
                        modifier = ExuraTextStyle.toModifier(
                            ExuraBodySizeTextVariant,
                            ExuraBaseWeightVariant,
                            ExuraSecondaryTextVariant
                        )
                    )
                }

                // Metadata
                Column(
                    modifier = Modifier.fillMaxWidth(),
                    verticalArrangement = Arrangement.spacedBy(8.px)
                ) {
                    series.relativeDay?.let { day ->
                        val absoluteDay = parentCollection?.relativeDay?.let { it + day } ?: day
                        InlineIcon(
                            icon = { UntiledIcon(Assets.IconsUntiled.CALENDAR_CLEAN) }
                        ) {
                            SpanText(
                                "${planLocalization.current.day} $absoluteDay",
                                modifier = ExuraTextStyle.toModifier(
                                    ExuraUnderwriteTextVariant,
                                    ExuraBaseWeightVariant,
                                    ExuraBaseColorVariant
                                )
                            )
                        }
                    }

                    series.durationInDays()?.let { duration ->
                        InlineIcon(
                            icon = { UntiledIcon(Assets.IconsUntiled.DURATION) }
                        ) {
                            SpanText(
                                planLocalization.current.daysCount(duration),
                                modifier = ExuraTextStyle.toModifier(
                                    ExuraUnderwriteTextVariant,
                                    ExuraBaseWeightVariant,
                                    ExuraBaseColorVariant
                                )
                            )
                        }
                    }

                    InlineIcon(
                        icon = { UntiledIcon(Assets.IconsUntiled.COMMUNICATES_ALL) }
                    ) {
                        SpanText(
                            planLocalization.current.series.communiquesCount(series.elements.size),
                            modifier = ExuraTextStyle.toModifier(
                                ExuraUnderwriteTextVariant,
                                ExuraBaseWeightVariant,
                                ExuraBaseColorVariant
                            )
                        )
                    }
                }
            }
        }
    }
}
